import React, {Component} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  ActivityIndicator,
  Dimensions,
  StyleSheet,
} from 'react-native';
import {AntDesign} from '@expo/vector-icons';
import {noticeStyles, noticeTones} from './boxChampNoticeStyles';
import modalStyles from '../../style/boxChampCalculateModal';

export function normalizeDimUnit(unit) {
  const s = String(unit || '').trim().toLowerCase();
  return s === 'cm' || s === 'centimeter' || s === 'centimeters' ? 'centimeter' : 'inches';
}

export function getItemsNeedingDims(unscannedItems) {
  const seen = new Set();
  const items = [];
  const pick = it => ({
    product_id: it.product_id,
    name: it.name,
    sku: it.sku,
    length: it.length,
    width: it.width,
    height: it.height,
    weight: it.weight,
    dim_unit: it.dim_unit,
  });
  (unscannedItems || []).forEach(item => {
    if (item.needs_dims && item.product_id && !seen.has(item.product_id)) {
      seen.add(item.product_id);
      items.push(pick(item));
    }
    (item.child_items || []).forEach(child => {
      if (child.needs_dims && child.product_id && !seen.has(child.product_id)) {
        seen.add(child.product_id);
        items.push(pick(child));
      }
    });
  });
  return items;
}

export default class BoxChampDimsForm extends Component {
  constructor(props) {
    super(props);
    this.state = {modalVisible: false, saving: false, rows: {}};
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState !== this.state) return true;
    const ids = list => (list || []).map(i => i.product_id).join(',');
    return (
      ids(this.props.items) !== ids(nextProps.items) ||
      this.props.weightUnit !== nextProps.weightUnit ||
      this.props.weightRequired !== nextProps.weightRequired ||
      this.props.onSaveAll !== nextProps.onSaveAll
    );
  }

  prefillFor(productId) {
    const item = (this.props.items || []).find(i => i.product_id === productId) || {};
    const str = v => (v != null && parseFloat(v) > 0 ? String(v) : '');
    return {
      length: str(item.length),
      width: str(item.width),
      height: str(item.height),
      weight: str(item.weight),
      dim_unit: normalizeDimUnit(item.dim_unit),
    };
  }

  getRow(productId) {
    return {...this.prefillFor(productId), ...this.state.rows[productId]};
  }

  isRowValid(productId) {
    const row = this.getRow(productId);
    const dimsValid =
      parseFloat(row.length) > 0 &&
      parseFloat(row.width) > 0 &&
      parseFloat(row.height) > 0;
    if (this.props.weightRequired === false) return dimsValid;
    return dimsValid && parseFloat(row.weight) > 0;
  }

  validItems() {
    return (this.props.items || []).filter(it => this.isRowValid(it.product_id));
  }

  setField(productId, field, val) {
    this.setState(prev => ({
      rows: {...prev.rows, [productId]: {...(prev.rows[productId] || {}), [field]: val}},
    }));
  }

  setUnit(productId, unit) {
    this.setField(productId, 'dim_unit', unit);
  }

  openModal = () => {
    this.setState({modalVisible: true});
    if (this.props.onModalToggle) this.props.onModalToggle(true);
  };

  closeModal = () => {
    if (this.state.saving) return;
    this.setState({modalVisible: false});
    if (this.props.onModalToggle) this.props.onModalToggle(false);
  };

  handleSaveAll = () => {
    const valid = this.validItems();
    if (valid.length === 0 || this.state.saving) return;
    const toOneDp = v => Math.round(parseFloat(v) * 10) / 10;
    const payloads = valid.map(it => {
      const row = this.getRow(it.product_id);
      return {
        product_id: it.product_id,
        data: {
          basicinfo: {
            id: it.product_id,
            length: toOneDp(row.length),
            width: toOneDp(row.width),
            height: toOneDp(row.height),
            dim_unit: row.dim_unit || 'inches',
          },
          weight: parseFloat(row.weight) || 0,
        },
      };
    });
    this.setState({saving: true});
    const done = () => {
      this.setState({saving: false, modalVisible: false});
      if (this.props.onModalToggle) this.props.onModalToggle(false);
    };
    const fail = () => this.setState({saving: false});
    const result = this.props.onSaveAll(payloads);
    if (result && typeof result.then === 'function') {
      result.then(done).catch(fail);
    } else {
      done();
    }
  };

  renderRow(item) {
    const row = this.getRow(item.product_id);
    const label = item.sku || item.name || `Product ${item.product_id}`;
    return (
      <View
        key={item.product_id}
        testID={`boxchamp-dims-row-${item.product_id}`}
        style={styles.row}>
        <Text style={styles.rowLabel}>{label}</Text>
        <View style={styles.inputs}>
          {['length', 'width', 'height'].map(field => (
            <View key={field} style={styles.inputGroup}>
              <Text style={styles.inputLabel}>{field[0].toUpperCase()}</Text>
              <TextInput
                testID={`boxchamp-dims-${field}-${item.product_id}`}
                keyboardType="decimal-pad"
                placeholder="0.0"
                placeholderTextColor="#7a94a8"
                value={row[field] || ''}
                editable={!this.state.saving}
                onChangeText={val => this.setField(item.product_id, field, val)}
                style={styles.input}
              />
            </View>
          ))}
          <View style={styles.unitToggle}>
            {['inches', 'centimeter'].map(unit => (
              <TouchableOpacity
                key={unit}
                testID={`boxchamp-dims-unit-${unit}-${item.product_id}`}
                disabled={this.state.saving}
                onPress={() => this.setUnit(item.product_id, unit)}
                style={[styles.unitBtn, row.dim_unit === unit && styles.unitBtnActive]}>
                <Text
                  style={[styles.unitBtnText, row.dim_unit === unit && styles.unitBtnTextActive]}>
                  {unit === 'inches' ? 'in' : 'cm'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {this.props.weightRequired !== false && (
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Wt</Text>
              <TextInput
                testID={`boxchamp-dims-weight-${item.product_id}`}
                keyboardType="decimal-pad"
                placeholder="0.0"
                placeholderTextColor="#7a94a8"
                value={row.weight || ''}
                editable={!this.state.saving}
                onChangeText={val => this.setField(item.product_id, 'weight', val)}
                style={styles.input}
              />
              <Text style={styles.unitSuffix}>{this.props.weightUnit || 'oz'}</Text>
            </View>
          )}
        </View>
      </View>
    );
  }

  render() {
    const {items} = this.props;
    if (!items || items.length === 0) return null;

    const count = items.length;
    const noun = count === 1 ? 'product needs' : 'products need';
    const need = this.props.weightRequired === false ? 'dimensions' : 'dimensions/weight';
    const canSave = this.validItems().length > 0 && !this.state.saving;
    const windowWidth = Dimensions.get('window').width;

    return (
      <View testID="boxchamp-dims-form">
        {/* Trigger banner — non-blocking; opens the entry modal. */}
        <TouchableOpacity
          testID="boxchamp-dims-banner"
          onPress={this.openModal}
          style={[noticeStyles.banner, styles.banner, {backgroundColor: noticeTones.info.background}]}>
          <Text style={[noticeStyles.text, {color: noticeTones.info.text}]}>
            {`${count} ${noun} ${need}`}
          </Text>
          <Text style={[styles.bannerAction, {color: noticeTones.info.text}]}>Enter Dimensions</Text>
        </TouchableOpacity>

        <Modal
          transparent
          visible={this.state.modalVisible}
          animationType="fade"
          onRequestClose={this.closeModal}>
          <View style={modalStyles.overlay}>
            <View
              testID="boxchamp-dims-modal"
              style={[modalStyles.dialog, {width: windowWidth >= 900 ? '55%' : '95%'}]}>
              {/* Header */}
              <View style={modalStyles.header}>
                <Text style={modalStyles.headerTitle}>
                  {`${this.props.weightRequired === false ? 'Enter Dimensions' : 'Enter Dimensions & Weight'} — ${count} ${count === 1 ? 'product' : 'products'}`}
                </Text>
                <TouchableOpacity
                  testID="boxchamp-dims-close"
                  onPress={this.closeModal}
                  style={modalStyles.closeIcon}>
                  <AntDesign name="close" size={20} color="#E0E7EF" />
                </TouchableOpacity>
              </View>

              {/* Body */}
              <View style={modalStyles.body}>
                <ScrollView style={modalStyles.scrollArea}>
                  {items.map(item => this.renderRow(item))}
                </ScrollView>
              </View>

              {/* Footer */}
              <View style={modalStyles.footer}>
                <TouchableOpacity
                  testID="boxchamp-dims-cancel"
                  onPress={this.closeModal}
                  style={modalStyles.cancelButton}>
                  <Text style={modalStyles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="boxchamp-dims-save-all"
                  onPress={this.handleSaveAll}
                  disabled={!canSave}
                  style={[
                    canSave ? modalStyles.queueButton : modalStyles.queueButtonDisabled,
                    styles.saveBtn,
                  ]}>
                  {this.state.saving && (
                    <ActivityIndicator
                      testID="boxchamp-dims-saving-spinner"
                      size="small"
                      color="#ffffff"
                      style={styles.saveSpinner}
                    />
                  )}
                  <Text style={canSave ? modalStyles.queueButtonText : modalStyles.queueButtonTextDisabled}>
                    Save
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  banner: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  bannerAction: {fontSize: 14, fontFamily: 'Poppins_600SemiBold', textDecorationLine: 'underline'},
  row: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#253c57',
  },
  rowLabel: {fontFamily: 'Poppins_500Medium', fontSize: 13, color: '#E0E7EF', marginBottom: 6},
  inputs: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6},
  inputGroup: {flexDirection: 'row', alignItems: 'center'},
  inputLabel: {fontSize: 12, marginRight: 3, color: '#9aafc0'},
  input: {
    borderWidth: 1,
    borderColor: '#3d5a73',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    width: 56,
    fontSize: 13,
    color: '#ffffff',
    backgroundColor: '#1a2a3a',
  },
  unitToggle: {
    flexDirection: 'row',
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#3d5a73',
  },
  unitBtn: {paddingHorizontal: 8, paddingVertical: 4, backgroundColor: '#1a2a3a'},
  unitBtnActive: {backgroundColor: '#336599'},
  unitBtnText: {fontSize: 12, color: '#9aafc0'},
  unitBtnTextActive: {color: '#ffffff'},
  unitSuffix: {fontSize: 12, marginLeft: 3, color: '#9aafc0'},
  saveBtn: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center'},
  saveSpinner: {marginRight: 6},
});
